import { CheckboxField } from 'lib/iterable-hook-form';
import { CampaignTypeFilter } from 'modules/domains/campaign';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ErrorText as ErrorMessage } from 'ui/formField';
import { body2, body2Bold, colors, spacing } from 'ui/theme';
import { HelpTooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { AllMessageMediums, GraphQLAlertMetric } from '../../types';
import { getTimeUnits } from '../../utils';
import { MetricType } from '../constants';
import { AlertFrequencyField, NumberThresholdField, PercentageThresholdField } from './AlertFields';
import { generateAlertDisplayText } from './AlertList.utils';
import { useAlertForm } from './useAlertForm';
import { MetricName } from '../../constants';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled.div<{ $isReadOnly: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  ${body2};
  gap: ${spacing.half};

  ${({ $isReadOnly }) => $isReadOnly && `color: ${colors.textSecondary};`};
`;

const BoldLabel = styled.span<{ $isReadOnly: boolean }>`
  ${body2Bold};

  ${({ $isReadOnly }) => $isReadOnly && `color: ${colors.textSecondary};`};
`;

const StyledCheckboxField = styled(CheckboxField)`
  margin-right: ${spacing.half};
`;

const TextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
  width: ${pxToRem(288)};

  @media (max-width: 768px) {
    width: 80%;
  }
`;

const ErrorText = styled(ErrorMessage)`
  margin-left: 0;
`;

type Props = {
  campaignType: CampaignTypeFilter;
  messageMedium: AllMessageMediums;
  metric: GraphQLAlertMetric;
};

export const ENABLED_ID = 'alert-enabled';
export const TOOLTIP_CONTENT = 'Not applicable for Gmail';

export const AlertListItem: React.FC<Props> = ({ metric, campaignType, messageMedium }: Props) => {
  const { metricName, valueType } = metric;
  const { metricDisplayName, comparator, preposition, suffix, verb } = useMemo(
    () => generateAlertDisplayText({ campaignType, metricName, valueType }),
    [campaignType, metricName, valueType],
  );

  const {
    frequency,
    status,
    statusName,
    thresholdName,
    frequencyName,
    thresholdError,
    frequencyError,
  } = useAlertForm({
    metric,
    campaignType,
    messageMedium,
  });

  const Threshold =
    valueType === MetricType.Number ? NumberThresholdField : PercentageThresholdField;

  return (
    metricDisplayName && (
      <Wrapper data-test={`${campaignType}-${metricName}-row`}>
        <ListItem $isReadOnly={!status}>
          <StyledCheckboxField data-test={ENABLED_ID} name={statusName} />
          <TextWrapper>
            <BoldLabel $isReadOnly={!status}>{metricDisplayName}</BoldLabel> {verb} {comparator}
            {metric?.metricName === MetricName.TotalEmailComplaintRate && (
              <HelpTooltip
                isEnabled={true}
                tooltipContent={TOOLTIP_CONTENT}
                iconProps={{ isColorInherited: true }}
              />
            )}
          </TextWrapper>
          <Threshold isReadOnly={!status} name={thresholdName} />
          {preposition}
          <AlertFrequencyField isReadOnly={!status} name={frequencyName} />
          {getTimeUnits(frequency)}
          {suffix}
        </ListItem>
        <div>
          {!!thresholdError && <ErrorText>{thresholdError.message}</ErrorText>}
          {!!frequencyError && <ErrorText>{frequencyError.message}</ErrorText>}
        </div>
      </Wrapper>
    )
  );
};
